import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, Subscriber } from "rxjs";
import { Messages } from "../model/messages";
import { EnvironmentHelper } from "./env.helper";
import { Router } from "@angular/router";
import { AuthService } from "../auth.service";
import { DepartmentUser } from "../model/department-user";

@Injectable({ providedIn: "root" })
export class DepartmentUserService {
  private apiUrl: string;
  private byIdUrl: string;
  private _currentDepartmentUsers: BehaviorSubject<DepartmentUser[]>;
  public selectedDepartmentUser: BehaviorSubject<DepartmentUser | null>;
  private _formState = new BehaviorSubject<Partial<DepartmentUser>>({});

  constructor(
    private http: HttpClient,
    private env: EnvironmentHelper,
    private router: Router,
    private auth: AuthService
  ) {
    this._currentDepartmentUsers = new BehaviorSubject<DepartmentUser[]>([]);
    this.selectedDepartmentUser = new BehaviorSubject<DepartmentUser | null>(
      null
    );
    this.apiUrl = `${env.apiBaseUrl}departmentuser/`;
    this.byIdUrl = `${env.apiBaseUrl}departmentuser/`;
  }

  public getAll(token: string): Observable<Messages> {
    const url = `${this.apiUrl}`;
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(url, {
          headers: { Authorization: "Bearer " + token },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveDepartmentUsers(response, observer),
          (error) => this.receiveDepartmentUsers(error, observer)
        );
    });
  }

  public getById(token: string, id: number): Observable<Messages> {
    const url = `${this.byIdUrl}by-id/${id}`;
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(url, {
          headers: { Authorization: "Bearer " + token },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveDepartmentUser(response, observer),
          (error) => this.receiveDepartmentUser(error, observer)
        );
    });
  }

  public getByUser(token: string, userId: number): Observable<Messages> {
    const url = `${this.apiUrl}by-user/${userId}`;
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(url, {
          headers: { Authorization: "Bearer " + token },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveDepartmentUsers(response, observer),
          (error) => this.receiveDepartmentUsers(error, observer)
        );
    });
  }
  public createDepartmentUser(
    token: string,
    departmentUser: DepartmentUser
  ): Observable<Messages> {
    const url = `${this.apiUrl}`;
    return new Observable<Messages>((observer) => {
      this.http
        .post<any>(url, departmentUser, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveDepartmentUser(response, observer),
          (error) => this.receiveDepartmentUser(error, observer)
        );
    });
  }

  public getByDepartment(
    token: string,
    departmentId: number
  ): Observable<Messages> {
    const url = `${this.apiUrl}by-department/${departmentId}`;
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(url, {
          headers: { Authorization: "Bearer " + token },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveDepartmentUsers(response, observer),
          (error) => this.receiveDepartmentUsers(error, observer)
        );
    });
  }
  public deleteDepartmentUser(token: string, id: number): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${id}`;
    return new Observable<Messages>((observer) => {
      this.http
        .delete<any>(url, {
          headers: { Authorization: "Bearer " + token },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveDepartmentUser(response, observer),
          (error) => this.receiveDepartmentUser(error, observer)
        );
    });
  }

  private receiveDepartmentUsers(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<DepartmentUser[]>)?.body;
      if (!data) {
        observer.next(
          Messages.from({ departmentUser: ["departmentUser.unknown"] })
        );
        return;
      }
      this._currentDepartmentUsers.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "auth",
          "unauthorized.access."
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "departmentUser",
          "departmentUser.incomplete"
        )
      );
    }
  }

  private receiveDepartmentUser(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<DepartmentUser>)?.body;
      if (!data) {
        observer.next(
          Messages.from({ departmentUser: ["departmentUser.unknown"] })
        );
        return;
      }
      this.selectedDepartmentUser.next(data);
      observer.next(Messages.EMPTY);
    } else if (
      response.status === 400 ||
      response.status === 401 ||
      response.status === 409 ||
      response.status === 500
    ) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "departmentUser",
          "departmentUser.load.error."
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "general",
          "an.unexpected.error.has.occurred."
        )
      );
    }
  }

  public selectFromRouter() {
    const segments = this.getLastSuccessfulNavigationSegments();
    if (segments && segments.length > 0) {
      const id = segments[segments.length - 1];
      const token = this.auth.token;
      this.getById(token, Number(id)).subscribe();
    } else {
      const emptyUser = new DepartmentUser(0, 0, 0);
      this.select(emptyUser);
    }
  }

  private getLastSuccessfulNavigationSegments(): string[] | null {
    const segments =
      this.router.lastSuccessfulNavigation?.finalUrl?.root.children["primary"]
        ?.segments;
    return segments ? segments.map((segment) => segment.path) : null;
  }

  public select(departmentUser: DepartmentUser) {
    this.selectedDepartmentUser.next(departmentUser);
  }

  public accept(departmentUsers: DepartmentUser[]) {
    this._currentDepartmentUsers.next(departmentUsers);
  }

  public get selectedDepartmentUserObservable(): Observable<DepartmentUser | null> {
    return this.selectedDepartmentUser.asObservable();
  }

  public get departmentUserObservable(): Observable<DepartmentUser[]> {
    return this._currentDepartmentUsers.asObservable();
  }

  public get DepartmentUserData(): DepartmentUser[] {
    return this._currentDepartmentUsers.value;
  }

  public get currentDepartmentUser(): DepartmentUser {
    const value = this.selectedDepartmentUser.value;
    return value || DepartmentUser.EMPTY;
  }

  get formState(): Observable<Partial<DepartmentUser>> {
    return this._formState.asObservable();
  }

  setFormState(state: Partial<DepartmentUser>) {
    localStorage.setItem("departmentUserFormBackup", JSON.stringify(state));
    this._formState.next(state);
  }

  restoreFormState(emit: boolean = true): Partial<DepartmentUser> {
    const savedState = localStorage.getItem("departmentUserFormBackup");
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        if (emit) {
          this._formState.next(parsedState);
        }
        return parsedState;
      } catch (e) {}
    }
    return {};
  }

  clearFormStateBackup() {
    localStorage.removeItem("departmentUserFormBackup");
  }
  public get currentDepartmentUsers(): BehaviorSubject<DepartmentUser[]> {
    return this._currentDepartmentUsers;
  }
}
